import { useParams, useSearchParams } from 'react-router-dom';
import { formatPlainDate } from '@cfl/shared';

import {
  Alert,
  AlertDescription,
  Badge,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
} from '@/components/cargofl-ui';
import { EmptyState } from '@/components/empty-state';
import { CalendarArt } from '@/components/illustrations';
import { AssessmentDetail } from '@/components/assessments/assessment-detail';
import { PageContainer } from '@/components/shell/page-container';
import { PILL_CLASS, windowReasonVariant, WINDOW_REASON_LABELS } from '@/lib/badges';
import { useCycles, type Cycle, type Quarter } from '@/lib/goals-api';
import { cn } from '@/lib/utils';

/**
 * Quarter strip.
 *
 * All four quarters, always, with their window state — because "can I submit yet,
 * and if not when" is the question, and a picker showing only the open one hides
 * the answer. An HR-forced window is amber even when it is OPEN: that it was opened
 * manually is something the employee and every later reviewer should notice.
 */
function QuarterStrip({
  quarters,
  selectedId,
  currentIndex,
  onSelect,
}: {
  quarters: Quarter[];
  selectedId: string;
  currentIndex: number | null;
  onSelect: (id: string) => void;
}) {
  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
      {quarters.map((q) => {
        const selected = q.id === selectedId;
        return (
          <button
            key={q.id}
            type="button"
            onClick={() => onSelect(q.id)}
            className={cn(
              'rounded-lg border p-3 text-left transition-colors',
              selected ? 'border-primary bg-accent' : 'hover:bg-muted',
            )}
          >
            <div className="flex items-center justify-between gap-2">
              <span className="font-semibold">
                {q.label}
                {currentIndex === q.index && (
                  <span className="ml-1.5 text-xs font-normal text-primary">current</span>
                )}
              </span>
              <Badge variant={windowReasonVariant(q.window.reason)} className={PILL_CLASS}>
                {WINDOW_REASON_LABELS[q.window.reason]}
              </Badge>
            </div>
            <p className="mt-1 text-xs text-muted-foreground">
              {formatPlainDate(q.startsOn)} – {formatPlainDate(q.endsOn)}
            </p>
            <p className="mt-0.5 text-xs text-muted-foreground">
              {q.window.open
                ? q.window.closesInDays !== null
                  ? `${q.window.closesInDays} day(s) left to submit`
                  : 'Open'
                : q.window.opensInDays !== null
                  ? `Opens in ${q.window.opensInDays} day(s)`
                  : `Window was ${formatPlainDate(q.window.opensOn)} – ${formatPlainDate(q.window.closesOn)}`}
            </p>
            {q.forcedByName && (
              <p className="mt-1 text-xs text-warning">
                {q.forcedState === 'FORCED_OPEN' ? 'Opened' : 'Closed'} by {q.forcedByName}
              </p>
            )}
          </button>
        );
      })}
    </div>
  );
}

/**
 * The quarter to land on: the one today falls in, unless its window is shut and HR has
 * forced an earlier quarter open - then that one, since it is the one people can submit.
 * Else the first.
 */
function defaultQuarter(cycle: Cycle): Quarter | undefined {
  const current = cycle.quarters.find((q) => q.index === cycle.currentQuarterIndex);
  if (current && !current.window.open) {
    const forced = cycle.quarters
      .filter((q) => q.index < current.index && q.forcedState === 'FORCED_OPEN' && !q.frozen && q.window.open)
      .sort((a, b) => b.index - a.index)[0];
    if (forced) return forced;
  }
  return current ?? cycle.quarters[0];
}

function QuarterPage({ employeeId }: { employeeId: string | 'me' }) {
  const { data: cycles, isPending, error } = useCycles();
  const [params, setParams] = useSearchParams();
  const quarterParam = params.get('quarter');

  if (isPending) return <Skeleton className="h-24 w-full" />;
  if (error) {
    return (
      <Alert variant="destructive">
        <AlertDescription>{error.message}</AlertDescription>
      </Alert>
    );
  }

  // The year is derived from the selected quarter rather than a second URL param —
  // one id cannot disagree with itself. Default to the year today falls in, not the
  // newest row: opening on FY2027-28 because HR set it up early is the same bug as
  // opening on Q1 in October.
  const cycle =
    cycles.find((c) => c.quarters.some((q) => q.id === quarterParam)) ??
    cycles.find((c) => c.currentQuarterIndex !== null) ??
    cycles[0];

  if (!cycle) {
    return (
      <EmptyState
        art={CalendarArt}
        title="No assessment year has been set up yet"
        body="Once HR opens the year, your self-assessment and your manager’s review appear here."
      />
    );
  }

  const selectedId = cycle.quarters.some((q) => q.id === quarterParam)
    ? quarterParam!
    : defaultQuarter(cycle)?.id;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-muted-foreground">
          {formatPlainDate(cycle.startsOn)} – {formatPlainDate(cycle.endsOn)}
        </p>
        <div className="flex items-center gap-2">
          <Label htmlFor="assessment-year" className="text-sm text-muted-foreground">
            Assessment year
          </Label>
          <Select
            value={cycle.id}
            onValueChange={(id) => {
              const next = cycles.find((c) => c.id === id);
              const quarter = next && defaultQuarter(next);
              if (quarter) setParams({ quarter: quarter.id });
            }}
          >
            <SelectTrigger id="assessment-year" className="w-[200px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {cycles.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  {c.label}
                  {c.currentQuarterIndex !== null && ' · current'}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <QuarterStrip
        quarters={cycle.quarters}
        selectedId={selectedId ?? ''}
        currentIndex={cycle.currentQuarterIndex}
        onSelect={(id) => setParams({ quarter: id })}
      />
      {selectedId && <AssessmentDetail employeeId={employeeId} quarterId={selectedId} />}
    </div>
  );
}

export function MyAssessmentsPage() {
  return (
    <PageContainer>
      <QuarterPage employeeId="me" />
    </PageContainer>
  );
}

/** A report's assessment, for the manager reviewing it. */
export function EmployeeAssessmentPage() {
  const { employeeId } = useParams<{ employeeId: string }>();
  return (
    <PageContainer>
      {employeeId ? <QuarterPage employeeId={employeeId} /> : null}
    </PageContainer>
  );
}
